import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './OnboardingChecklistWidget.css';

interface OnboardingStep {
    id: number;
    title: string;
    description: string;
    actionLabel?: string;
    actionUrl?: string;
    isExternal?: boolean;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
    {
        id: 1,
        title: 'İlk İş Yerinizi Ekleyin',
        description: 'Sol kartta bulunan "YENİ İŞ YERİ EKLE" kartına ilk iş yerinizi kaydedin (sağ üstte bulunan kart/liste seçim butonları ile görünümü değiştirebilirsiniz).',
        actionLabel: 'İş Yerlerine Git',
        actionUrl: '/dashboard'
    },
    {
        id: 2,
        title: 'İş Yeri Detay Kartını Açın',
        description: 'Kayıtlı olan bir iş yerine tıkladığınızda iş yeri detay kartı açılacaktır.',
        actionLabel: 'İş Yerini Seç',
        actionUrl: '/dashboard'
    },
    {
        id: 3,
        title: 'İSG Evrakları Yönetimi',
        description: 'Açılan kartta İSG EVRAKLARI sekmesinden evrak ekleyebilir, görüntüleyebilir ve takibini yapabilirsiniz.'
    },
    {
        id: 4,
        title: 'Ziyaret Planlayın',
        description: 'ZİYARET PLANLA ile günlük/haftalık/aylık ziyaret planları yapabilir, müşterileriniz ile paylaşabilirsiniz.',
        actionLabel: 'Planlamaya Git',
        actionUrl: '/planning'
    },
    {
        id: 5,
        title: 'Yapılan Ziyaretler & Saha Raporu',
        description: 'YAPILAN ZİYARETLER sekmesinde aylık ziyaretlerinizi işaretleyebilir ve sahada saha raporu girebilirsiniz.',
        actionLabel: 'Ziyaretler',
        actionUrl: '/visits'
    },
    {
        id: 6,
        title: 'Firma Bilgileri, Notlar & GPS',
        description: 'FİRMA BİLGİLERİ/NOTLAR sekmesinde işyerine ait detayları görebilir; adres girerek mobilinizle senkronize çalışan sistem sayesinde GPS özelliğini kullanabilirsiniz.'
    },
    {
        id: 7,
        title: 'Ana Sayfa Planlama',
        description: 'ANA SAYFA PLANLAMA butonu ile kaydetmiş olduğunuz planlarınızı görebilir, ekleyebilir, değiştirebilirsiniz.',
        actionLabel: 'Planlama Takvimi',
        actionUrl: '/planning'
    },
    {
        id: 8,
        title: 'İstatistik & Eksiklik Takibi',
        description: 'İSTATİSTİK butonu ile tüm iş yerlerinize ait yapılan çalışmaları (eksiklikler de dahil olmak üzere) tek sayfada görüntüleyebilirsiniz.',
        actionLabel: 'İstatistikler',
        actionUrl: '/statistics'
    },
    {
        id: 9,
        title: 'Arşiv Yönetimi',
        description: 'ARŞİV YÖNETİMİ ile kaydetmiş olduğunuz belgelerinizi görüntüleyebilirsiniz.',
        actionLabel: 'Arşive Git',
        actionUrl: '/archive'
    },
    {
        id: 10,
        title: 'Otomatik Belge Yönetimi',
        description: 'BELGE YÖNETİMİ sayfasında Risk Analizi / Acil Eylem Planı / Yıllık Eğitim ve Çalışma Planları ve Sertifikalarınızı OTOMATİK olarak hazırlatabilirsiniz.',
        actionLabel: 'Belge Motoru',
        actionUrl: '/documents'
    }
];

const TOTAL_SETUP_STEPS = ONBOARDING_STEPS.length; // 10

// 4 Kasım 2026 00:00 UTC ve sonrası (bu tarihten sonra kayıt olan yeni uzmanlara göster)
const FEATURE_LAUNCH_TIMESTAMP = new Date('2026-11-04T00:00:00Z').getTime();

export const OnboardingChecklistWidget: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const userId = user?.id || 'guest';
    const storageKeySteps = `uzman_onboarding_steps_${userId}`;
    const storageKeyMinimized = `uzman_onboarding_minimized_${userId}`;
    const storageKeyFinished = `uzman_onboarding_finished_${userId}`;

    // State: Kullanıcının tamamladığı adımlar
    const [completedSteps, setCompletedSteps] = useState<number[]>(() => {
        try {
            const saved = localStorage.getItem(storageKeySteps);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    // Simge durumuna küçültülmüş mü?
    // Her yeni girişte açık başlar, kullanıcı unutmasın
    const [isMinimized, setIsMinimized] = useState<boolean>(false);

    // Kullanıcı %100 tamamlayıp kendi kapattı mı?
    const [isFinished, setIsFinished] = useState<boolean>(() => {
        try {
            return localStorage.getItem(storageKeyFinished) === 'true';
        } catch {
            return false;
        }
    });

    // LocalStorage senkronizasyonu
    useEffect(() => {
        try {
            localStorage.setItem(storageKeySteps, JSON.stringify(completedSteps));
        } catch (e) {
            console.error('Failed to save onboarding steps', e);
        }
    }, [completedSteps, storageKeySteps]);

    const toggleStep = (stepId: number) => {
        setCompletedSteps(prev => {
            const next = prev.includes(stepId) ? prev.filter(id => id !== stepId) : [...prev, stepId];
            return next;
        });
    };

    const handleActionClick = (step: OnboardingStep) => {
        if (!step.actionUrl) return;

        if (step.isExternal) {
            window.open(step.actionUrl, '_blank', 'noopener,noreferrer');
        } else {
            navigate(step.actionUrl);
        }
    };

    const handleToggleMinimize = (min: boolean) => {
        setIsMinimized(min);
        try {
            // Oturumlar arasi gizli kalmasin diye kaydetmiyoruz
        } catch {}
    };

    const handleFinishAndClose = () => {
        setIsFinished(true);
        try {
            localStorage.setItem(storageKeyFinished, 'true');
        } catch {}
    };

    // Filtreleme:
    // 1. Bitirip kapattıysa gösterme
    if (isFinished) {
        return null;
    }

    // 2. Oturum açmamışsa gösterme
    if (!isAuthenticated) {
        return null;
    }

    // 3. Eski kullanıcı kontrolü (Yalnızca yeni kayıtlar veya onboarding başlatmış olanlar)
    const userCreatedAt = user?.createdAt ? new Date(user.createdAt).getTime() : 0;
    const isRegisteredAfterLaunch = userCreatedAt >= FEATURE_LAUNCH_TIMESTAMP;
    const hasStartedOnboarding = completedSteps.length > 0;

    if (!isRegisteredAfterLaunch && !hasStartedOnboarding && userCreatedAt > 0) {
        return null;
    }

    const completedCount = completedSteps.length;
    const progressPercent = Math.round((completedCount / TOTAL_SETUP_STEPS) * 100);
    const allCompleted = completedCount >= TOTAL_SETUP_STEPS;

    // Küçültülmüşse sağ altta rozet olarak dursun
    if (isMinimized) {
        return (
            <button
                className="onboarding-widget-floating-trigger"
                onClick={() => handleToggleMinimize(false)}
                title="Hızlı Başlangıç Rehberi (%100 tamamlanana kadar açık kalır)"
            >
                <span>🚀 Başlangıç Rehberi</span>
                <span className="onboarding-widget-progress-badge">
                    {allCompleted ? '✓ %100' : `%${progressPercent}`}
                </span>
            </button>
        );
    }

    return (
        <div className="onboarding-widget-panel">
            {/* Başlık Şeridi */}
            <div className="onboarding-widget-header">
                <div className="onboarding-widget-header-title">
                    <span>🧭</span>
                    <span>Hızlı Başlangıç Rehberi</span>
                </div>
                <div className="onboarding-widget-header-actions">
                    <button
                        className="onboarding-widget-icon-btn"
                        onClick={() => handleToggleMinimize(true)}
                        title="Simge Durumuna Küçült"
                    >
                        −
                    </button>
                </div>
            </div>

            {/* İlerleme Çubuğu */}
            <div className="onboarding-widget-progress-section">
                <div className="onboarding-widget-progress-labels">
                    <span>Kurulum Adımları</span>
                    <span style={{ fontWeight: 700, color: allCompleted ? '#16a34a' : '#2563eb' }}>
                        %{progressPercent} ({completedCount}/{TOTAL_SETUP_STEPS})
                    </span>
                </div>
                <div className="onboarding-widget-progress-bar-bg">
                    <div
                        className="onboarding-widget-progress-bar-fill"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            </div>

            {/* Adımlar Gövdesi */}
            <div className="onboarding-widget-body">
                {!allCompleted && (
                    <div style={{ background: '#f8fafc', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', color: '#475569', border: '1px dashed #cbd5e1' }}>
                        💡 Kurulum <strong>%100 tamamlanana kadar</strong> kaç kere çıkıp girseniz de bu rehber kaldığınız yerden sizi yönlendirmeye devam edecektir.
                    </div>
                )}

                {/* 1'den 10'a Kurulum Adımları */}
                {ONBOARDING_STEPS.map(step => {
                    const isDone = completedSteps.includes(step.id);
                    return (
                        <div
                            key={step.id}
                            className={`onboarding-step-item ${isDone ? 'is-completed' : ''}`}
                        >
                            <input
                                type="checkbox"
                                checked={isDone}
                                onChange={() => toggleStep(step.id)}
                                className="onboarding-step-checkbox"
                                id={`uzman-step-${step.id}`}
                            />
                            <div className="onboarding-step-details">
                                <div className="onboarding-step-header">
                                    <span className="onboarding-step-number">{step.id}</span>
                                    <label
                                        htmlFor={`uzman-step-${step.id}`}
                                        className="onboarding-step-title"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {step.title}
                                    </label>
                                </div>
                                <p className="onboarding-step-desc">{step.description}</p>
                                {step.actionLabel && step.actionUrl && (
                                    <button
                                        type="button"
                                        onClick={() => handleActionClick(step)}
                                        className="onboarding-step-action-btn"
                                    >
                                        <span>{step.actionLabel}</span>
                                        <span>→</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}

                {/* 10 Adım Tamamlandıysa (%100) Kutlama ve Kapatma Kartı En Altta */}
                {allCompleted && (
                    <div className="onboarding-celebration-card" style={{ marginTop: '8px' }}>
                        <div className="onboarding-celebration-title">
                            🎉 ARTIK BAŞLAYABİLİRİZ!
                        </div>
                        <p className="onboarding-celebration-desc" style={{ marginBottom: '12px' }}>
                            Tüm adımları başarıyla tamamladınız. Uzman Destek sistemini kullanmaya tamamen hazırsınız!
                        </p>
                        <button
                            type="button"
                            onClick={handleFinishAndClose}
                            style={{
                                background: '#166534',
                                color: '#ffffff',
                                border: 'none',
                                padding: '10px 16px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                width: '100%'
                            }}
                        >
                            ✓ Kurulumu Tamamla ve Kılavuzu Kapat
                        </button>
                    </div>
                )}
            </div>

            {/* Alt Çubuk (WhatsApp Canlı Destek) */}
            <div className="onboarding-widget-footer">
                <a
                    href="https://wa.me/905072136474?text=Merhaba,%20Uzman%20Destek%20kullan%C4%B1m%20ad%C4%B1mlar%C4%B1nda%20deste%C4%9Fe%20ihtiyac%C4%B1m%20var,%20yard%C4%B1mc%C4%B1%20olur%20musunuz?"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="onboarding-whatsapp-btn"
                >
                    <span>💬</span>
                    <span>Teknik Destek: 0507 213 64 74</span>
                </a>
            </div>
        </div>
    );
};

export default OnboardingChecklistWidget;
